import { Vector3 } from 'three';
import type { GameContext } from '../core/context';
import { DEG, clamp, damp, forward, type V2 } from '../core/math';
import type { System } from '../core/system';
import { Trauma, impactTrauma, shakeNoise } from '../fx/juice';
import { touchUi } from '../input/touch';
import { cssSize } from '../render/cssSize';
import type { CityLayout } from '../world/types';
import { phoneFrameOffset, viewExtents } from './framing';
import { SmoothPoint, SmoothValue, zoomSpeedStep } from './smooth';

/**
 * High, angled top-down camera.
 *
 * The rig follows a *focus* (the player or their vehicle) supplied by the player module through
 * `setFocus`. It looks ahead along the focus velocity, pulls back with speed, and supports
 * trauma-based shake and short cinematic overrides that keep the top-down perspective.
 *
 * `yaw` is the heading of "screen up" (default PI: screen up = -Z = north).
 */
export interface CameraFocus {
  pos: V2;
  vel: V2;
  /** True when driving: wider framing and stronger look-ahead. */
  inVehicle: boolean;
}

export interface CameraOverride {
  /** Ground point to frame. */
  target: V2;
  /** Distance from target along the view ray (m). */
  distance?: number;
  /** Elevation angle in degrees (90 = straight down). */
  pitchDeg?: number;
  /** Extra yaw in degrees relative to normal. */
  yawOffsetDeg?: number;
  /** Seconds before the override releases automatically (default: until cleared). */
  duration?: number;
  /** Blend rate (1/s); default 2.5. */
  blend?: number;
  /** Look at this height above the ground (m) instead of the road (Pike's spray bay orbits a car's body). */
  lookHeight?: number;
  /** Rate (1/s) for pitch, yaw and distance too (default: the gentle cinematic rates). An orbit wants it quick. */
  orbitRate?: number;
}

export interface CameraRig {
  yaw: number;
  /** Tunables (live-editable from the console via __bh). */
  readonly params: CameraParams;
  /** Player's zoom setting: multiplies the follow distances (not cinematic beats). */
  distanceScale: number;
  setFocus(focus: CameraFocus): void;
  /**
   * What the player is aiming at on foot (weapons: the lock), or null. The frame leans part of the way
   * towards it so a target down the screen stays in view (docs/CONTROLS-POLISH.md).
   */
  setAim(target: V2 | null): void;
  /** Add trauma 0..1 (stacks, decays). 0.2 = bump, 0.5 = heavy crash, 1 = explosion. */
  shake(amount: number): void;
  /** Kick the frame `amount` metres along `dir` (unit, ground plane) at once, eased back over ~0.2 s: a blow landing. */
  nudge(dir: V2, amount: number): void;
  setOverride(o: CameraOverride | null): void;
  /**
   * Short cinematic beat for missions: frame `target` for `duration` seconds, then ease back to
   * normal follow. Keeps the top-down perspective (pitch defaults to a slightly lower angle).
   */
  cinematic(target: V2, duration: number, opts?: Omit<CameraOverride, 'target' | 'duration'>): void;
  readonly isCinematic: boolean;
  /** Jump to the target without smoothing (teleports, respawns). */
  snap(): void;
  /** Current ground point the camera centres on. */
  readonly lookAt: V2;
  /** Unit vector of "screen up" on the ground plane (for screen-relative movement). */
  screenUp(): V2;
  /** Unit vector of "screen right" on the ground plane. */
  screenRight(): V2;
}

export interface CameraParams {
  fov: number;
  pitchDeg: number;
  footDistance: number;
  driveDistance: number;
  /** Extra distance per m/s of speed. */
  speedPullback: number;
  maxDistance: number;
  lookAheadTime: number;
  maxLookAhead: number;
  /** Settle times (s) of the critically damped follow: the frame, the look-ahead, the zoom and FOV. */
  followSmooth: number;
  leadSmooth: number;
  zoomSmooth: number;
  /** Blend rate (1/s) of the distance during cinematic overrides. */
  zoomRate: number;
}

/**
 * Follow distances on a phone (docs/MOBILE.md "Camera on a phone"): closer at rest and about town, with a
 * steeper speed pull-back that meets the desk distance at 30 m/s, and a longer look-ahead (held in the band).
 */
export const PHONE = { footDistance: 36, driveDistance: 44, speedPullback: 1.1, lookAheadTime: 0.6 } as const;
/** The phone framing is for the touch layer on a wide screen (landscape phones); a tablet keeps the desk framing. */
export const PHONE_ASPECT = 1.5;

declare module '../core/services' {
  interface ServiceMap {
    cameraRig: CameraRig;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Cinematic camera beat: frame (x,z) for `seconds`. */
    /** `yawDeg`: turn the view (review shots of a wall the default north-up camera sees edge-on). */
    cameraFocus(x: number, z: number, seconds: number, distance?: number, pitchDeg?: number, yawDeg?: number, lookY?: number): void;
    cameraShake(amount: number): void;
    /** Screen up and right on the ground plane (unit vectors), to lay a scene out across the view. */
    cameraAxes(): { up: V2; right: V2 };
    /**
     * Framing read-out (docs/MOBILE.md "Camera on a phone"): follow distance, whether the phone framing is on,
     * where the focus (player or car) sits on screen as fractions of the width / height from the top left,
     * the focus footprint's screen box in CSS px, and how many metres of ground the view reaches from the
     * focus in each screen direction.
     */
    cameraFrame(): {
      distance: number;
      fov: number;
      phone: boolean;
      /** The smoothed look-ahead and junction bias (m, ground plane). */
      lead: V2;
      junction: V2;
      focus: { x: number; y: number };
      box: { w: number; h: number };
      view: { up: number; down: number; left: number; right: number };
    } | null;
  }
}

/** Aiming: the frame leans this share of the way to the target, at most AIM_LEAN_MAX m. */
const AIM_LEAN = 0.45;
const AIM_LEAN_MAX = 10;

export function createCameraRigSystem(): System {
  let ctx: GameContext;
  const params: CameraParams = {
    fov: 34,
    pitchDeg: 64,
    footDistance: 42,
    driveDistance: 54,
    speedPullback: 0.75,
    maxDistance: 92,
    lookAheadTime: 0.55,
    maxLookAhead: 21,
    followSmooth: 0.25,
    leadSmooth: 0.5,
    zoomSmooth: 0.7,
    zoomRate: 1.6,
  };
  const focus: CameraFocus = { pos: { x: 0, z: 0 }, vel: { x: 0, z: 0 }, inVehicle: false };
  const lookAt: V2 = { x: 0, z: 0 };
  const lead: V2 = { x: 0, z: 0 };
  const lookS = new SmoothPoint();
  const leadS = new SmoothPoint();
  const distS = new SmoothValue(params.footDistance);
  const fovS = new SmoothValue(params.fov);
  /** Speed the zoom follows (smooth.ts zoomSpeedStep). */
  let zoomSpeed = 0;
  let distance = params.footDistance;
  let pitch = params.pitchDeg * DEG;
  let yawOffset = 0;
  const trauma = new Trauma();
  const nudgeV: V2 = { x: 0, z: 0 };
  let fov = params.fov;
  let kick = 0;
  let time = 0;
  let override: CameraOverride | null = null;
  let overrideTime = 0;
  let snapNext = true;
  let lookY = 0;
  let layout: CityLayout | null = null;
  const junctionBias: V2 = { x: 0, z: 0 };
  let aim: V2 | null = null;
  const aimLean: V2 = { x: 0, z: 0 };
  /** Phone framing is on (see update). */
  let phoneFrame = false;

  const rig: CameraRig = {
    yaw: Math.PI,
    params,
    distanceScale: 1,
    setFocus(f) {
      focus.pos.x = f.pos.x;
      focus.pos.z = f.pos.z;
      focus.vel.x = f.vel.x;
      focus.vel.z = f.vel.z;
      focus.inVehicle = f.inVehicle;
    },
    setAim(t) {
      aim = t ? { x: t.x, z: t.z } : null;
    },
    shake(amount) {
      trauma.add(amount);
    },
    nudge(dir, amount) {
      nudgeV.x = clamp(nudgeV.x + dir.x * amount, -0.6, 0.6);
      nudgeV.z = clamp(nudgeV.z + dir.z * amount, -0.6, 0.6);
    },
    setOverride(o) {
      override = o;
      overrideTime = 0;
    },
    snap() {
      snapNext = true;
    },
    cinematic(target, duration, opts = {}) {
      override = { pitchDeg: params.pitchDeg - 6, blend: 2.2, ...opts, target: { x: target.x, z: target.z }, duration };
      overrideTime = 0;
    },
    get isCinematic() {
      return override !== null;
    },
    lookAt,
    screenUp() {
      return forward(rig.yaw);
    },
    screenRight() {
      const f = forward(rig.yaw);
      // right = forward x up = (-f.z, f.x) in (x,z)... derived from right(h) = (-cos h, sin h)
      return { x: -f.z, z: f.x };
    },
  };

  return {
    id: 'camera',
    order: 600,
    init(c) {
      ctx = c;
      c.services.provide('cameraRig', rig);
      c.camera.fov = params.fov;
      c.camera.near = 1;
      c.camera.far = 600;
      c.camera.updateProjectionMatrix();
      c.events.on('playerTeleported', () => rig.snap());
      layout = c.services.tryGet('layout') ?? null;
      const playerVehicleId = (): number | null => c.services.tryGet('player')?.vehicle?.id ?? null;
      c.events.on('collision', (e) => {
        const pid = playerVehicleId();
        const mine = (e.a.kind === 'vehicle' && e.a.id === pid) || (e.b?.kind === 'vehicle' && e.b.id === pid) || e.a.kind === 'player';
        if (!e.isNew) return;
        const d = Math.hypot(e.pos.x - focus.pos.x, e.pos.z - focus.pos.z);
        const t = impactTrauma(e.speed, d, mine);
        if (t <= 0) return;
        rig.shake(t);
        // Small punch-in on big hits of our own, recovered smoothly in update().
        if (mine && e.speed > 8) kick = Math.min(1, kick + (e.speed - 8) / 20);
      });
      c.events.on('vehicleExploded', (e) => {
        const d = Math.hypot(e.pos.x - focus.pos.x, e.pos.z - focus.pos.z);
        rig.shake(Math.max(0, 1 - d / 60));
      });
      c.hooks.register('cameraFocus', (x, z, seconds, dist, pitch, yaw, lookY) =>
        rig.cinematic({ x, z }, seconds, {
          ...(dist !== undefined ? { distance: dist } : {}),
          ...(pitch !== undefined ? { pitchDeg: pitch } : {}),
          ...(yaw !== undefined ? { yawOffsetDeg: yaw } : {}),
          ...(lookY !== undefined ? { lookHeight: lookY } : {}),
        }),
      );
      c.hooks.register('cameraShake', (a) => rig.shake(a));
      c.hooks.register('cameraAxes', () => ({ up: rig.screenUp(), right: rig.screenRight() }));
      c.hooks.register('cameraFrame', () => {
        if (!c.render) return null;
        const cam = c.camera;
        cam.updateMatrixWorld();
        const css = cssSize(c.render.canvas);
        const v = new Vector3();
        /** Screen fraction (0..1 from the top left) of a ground point. */
        const frac = (x: number, y: number, z: number): { x: number; y: number } => {
          v.set(x, y, z).project(cam);
          return { x: (v.x + 1) / 2, y: (1 - v.y) / 2 };
        };
        /** Where the ray through screen point (nx, ny) meets the ground, in metres from the focus along `dir`. */
        const reach = (nx: number, ny: number, dir: V2): number => {
          v.set(nx, ny, 0.5).unproject(cam);
          const dx = v.x - cam.position.x;
          const dy = v.y - cam.position.y;
          const dz = v.z - cam.position.z;
          if (dy >= 0) return Infinity;
          const t = -cam.position.y / dy;
          const gx = cam.position.x + dx * t - focus.pos.x;
          const gz = cam.position.z + dz * t - focus.pos.z;
          return Math.round((gx * dir.x + gz * dir.z) * 10) / 10;
        };
        const up = rig.screenUp();
        const right = rig.screenRight();
        // The focus footprint: the car's collision box under its heading, or a 1 m square on foot.
        const player = c.services.tryGet('player');
        const veh = player?.vehicle ?? null;
        const len = veh ? veh.spec.length : 1;
        const wid = veh ? veh.spec.width : 1;
        const h = player?.heading ?? 0;
        const f = forward(h);
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        for (const [a, b] of [
          [1, 1],
          [1, -1],
          [-1, 1],
          [-1, -1],
        ]) {
          const px = focus.pos.x + f.x * (len / 2) * a! + -f.z * (wid / 2) * b!;
          const pz = focus.pos.z + f.z * (len / 2) * a! + f.x * (wid / 2) * b!;
          const s = frac(px, 0, pz);
          minX = Math.min(minX, s.x);
          maxX = Math.max(maxX, s.x);
          minY = Math.min(minY, s.y);
          maxY = Math.max(maxY, s.y);
        }
        return {
          distance: Math.round(distance * 10) / 10,
          fov: Math.round(fov * 10) / 10,
          phone: phoneFrame,
          lead: { x: Math.round(lead.x * 10) / 10, z: Math.round(lead.z * 10) / 10 },
          junction: { x: Math.round(junctionBias.x * 10) / 10, z: Math.round(junctionBias.z * 10) / 10 },
          focus: frac(focus.pos.x, 0, focus.pos.z),
          box: { w: Math.round((maxX - minX) * css.w), h: Math.round((maxY - minY) * css.h) },
          view: { up: reach(0, 1, up), down: -reach(0, -1, up), left: -reach(-1, 0, right), right: reach(1, 0, right) },
        };
      });
    },
    update(dt) {
      time += dt;
      const speed = Math.hypot(focus.vel.x, focus.vel.z);

      let targetX: number;
      let targetZ: number;
      let targetDist: number;
      let targetPitch = params.pitchDeg * DEG;
      let targetYawOffset = 0;
      let rate = 2.5;
      let targetFov = params.fov;
      let targetLookY = 0;
      let orbitRate = 0;

      if (override) {
        overrideTime += dt;
        targetX = override.target.x;
        targetZ = override.target.z;
        targetDist = override.distance ?? distance;
        if (override.pitchDeg !== undefined) targetPitch = override.pitchDeg * DEG;
        targetYawOffset = (override.yawOffsetDeg ?? 0) * DEG;
        rate = override.blend ?? 2.5;
        targetLookY = override.lookHeight ?? 0;
        orbitRate = override.orbitRate ?? 0;
        if (override.duration !== undefined && overrideTime >= override.duration) override = null;
      } else {
        // The zoom follows a speed that rises within a second and falls over a few (no pumping on pedal taps).
        zoomSpeed = zoomSpeedStep(zoomSpeed, speed, dt);
        phoneFrame = touchUi.enabled && ctx.camera.aspect >= PHONE_ASPECT;
        const P = phoneFrame ? PHONE : params;
        // Look ahead along velocity, more when driving.
        const la = focus.inVehicle ? P.lookAheadTime : P.lookAheadTime * 0.4;
        let lx = focus.vel.x * la;
        let lz = focus.vel.z * la;
        const ll = Math.hypot(lx, lz);
        if (ll > params.maxLookAhead) {
          lx *= params.maxLookAhead / ll;
          lz *= params.maxLookAhead / ll;
        }
        leadS.to(lx, lz, params.leadSmooth, dt);
        lead.x = leadS.x.value;
        lead.z = leadS.z.value;
        // Intersection framing: when driving toward a junction, drift the frame toward it so side
        // streets (and whatever is coming out of them) are visible before you arrive.
        let bx = 0;
        let bz = 0;
        if (focus.inVehicle && layout && speed > 6) {
          const dx = focus.vel.x / speed;
          const dz = focus.vel.z / speed;
          let best = Infinity;
          for (const it of layout.intersections) {
            const rx = it.pos.x - focus.pos.x;
            const rz = it.pos.z - focus.pos.z;
            const along = rx * dx + rz * dz;
            if (along < 6 || along > 50) continue;
            const side = Math.abs(rx * dz - rz * dx);
            if (side > 14 || along >= best) continue;
            best = along;
            const w = 0.35 * (1 - along / 50) * Math.min(1, (speed - 6) / 10);
            bx = (it.pos.x - (focus.pos.x + lead.x)) * w;
            bz = (it.pos.z - (focus.pos.z + lead.z)) * w;
          }
        }
        junctionBias.x += (bx - junctionBias.x) * damp(1.6, dt);
        junctionBias.z += (bz - junctionBias.z) * damp(1.6, dt);
        // Aiming on foot: lean towards the target (AIM_LEAN of the way, at most AIM_LEAN_MAX m) so the man you
        // have locked down the screen is not left under the bottom edge (the view reaches ~12 m below you).
        let ax = 0;
        let az = 0;
        if (aim && !focus.inVehicle) {
          ax = (aim.x - focus.pos.x) * AIM_LEAN;
          az = (aim.z - focus.pos.z) * AIM_LEAN;
          const al = Math.hypot(ax, az);
          if (al > AIM_LEAN_MAX) {
            ax *= AIM_LEAN_MAX / al;
            az *= AIM_LEAN_MAX / al;
          }
        }
        aimLean.x += (ax - aimLean.x) * damp(3, dt);
        aimLean.z += (az - aimLean.z) * damp(3, dt);
        const base = focus.inVehicle ? P.driveDistance : P.footDistance;
        targetDist = Math.min(base + zoomSpeed * P.speedPullback, params.maxDistance) * rig.distanceScale;
        if (focus.inVehicle) targetFov += clamp((zoomSpeed - 8) * 0.2, 0, 5);
        // Phone: centre the view between the screen edges and keep the look-ahead inside the thumb-free band.
        let ox = lead.x;
        let oz = lead.z;
        if (phoneFrame) {
          const o = phoneFrameOffset(lead, rig.screenUp(), viewExtents(targetDist, targetPitch, targetFov));
          // The follow spring trails a moving target by speed × followSmooth; feed that forward so the band
          // and the look-ahead mean what they say at speed.
          ox = o.x + focus.vel.x * params.followSmooth;
          oz = o.z + focus.vel.z * params.followSmooth;
        }
        targetX = focus.pos.x + ox + junctionBias.x + aimLean.x;
        targetZ = focus.pos.z + oz + junctionBias.z + aimLean.z;
      }

      if (snapNext) {
        lookAt.x = targetX;
        lookAt.z = targetZ;
        distance = targetDist;
        fov = targetFov;
        pitch = targetPitch;
        yawOffset = targetYawOffset;
        lookY = targetLookY;
        lookS.set(targetX, targetZ);
        leadS.set(lead.x, lead.z);
        distS.set(distance);
        fovS.set(fov);
        snapNext = false;
      } else if (override) {
        // Cinematic beats and the spray bay orbit keep their own blend rates.
        const k = damp(rate, dt);
        lookAt.x += (targetX - lookAt.x) * k;
        lookAt.z += (targetZ - lookAt.z) * k;
        distance += (targetDist - distance) * damp(orbitRate || params.zoomRate, dt);
        fov += (targetFov - fov) * damp(1.4, dt);
        pitch += (targetPitch - pitch) * damp(orbitRate || 2, dt);
        yawOffset += (targetYawOffset - yawOffset) * damp(orbitRate || 2, dt);
        lookY += (targetLookY - lookY) * damp(orbitRate || 2, dt);
        lookS.set(lookAt.x, lookAt.z);
        distS.set(distance);
        fovS.set(fov);
      } else {
        // Follow: critically damped, so a target that jumps (a wall, the pedal, reverse) bends the frame's
        // path instead of kinking it, and nothing overshoots (camera/smooth.ts).
        lookS.to(targetX, targetZ, params.followSmooth, dt);
        lookAt.x = lookS.x.value;
        lookAt.z = lookS.z.value;
        distance = distS.to(targetDist, params.zoomSmooth, dt);
        fov = fovS.to(targetFov, params.zoomSmooth, dt);
        pitch += (targetPitch - pitch) * damp(2, dt);
        yawOffset += (targetYawOffset - yawOffset) * damp(2, dt);
        lookY += (targetLookY - lookY) * damp(2, dt);
      }

      // Trauma shake: smoothed noise scaled by trauma², decays over ~0.7 s. Impact kick eases back out.
      trauma.update(dt);
      kick = Math.max(0, kick - dt * 3);
      const sh = trauma.amount();
      // A blow's kick: the whole frame jumps along the blow and eases back (on top of the noise).
      const nk = damp(11, dt);
      nudgeV.x -= nudgeV.x * nk;
      nudgeV.z -= nudgeV.z * nk;
      const sx = sh * 0.9 * (shakeNoise(time * 22, 1) + shakeNoise(time * 11, 2) * 0.5) + nudgeV.x;
      const sz = sh * 0.9 * (shakeNoise(time * 22, 3) + shakeNoise(time * 9, 4) * 0.5) + nudgeV.z;

      const yaw = rig.yaw + yawOffset;
      const back = forward(yaw); // screen-up direction; camera sits opposite it
      const shownDist = distance * (1 - 0.045 * kick * kick);
      const horiz = Math.cos(pitch) * shownDist;
      const height = Math.sin(pitch) * shownDist;
      const cam = ctx.camera;
      cam.position.set(lookAt.x - back.x * horiz + sx, height + lookY, lookAt.z - back.z * horiz + sz);
      cam.lookAt(lookAt.x + sx * 0.5, lookY, lookAt.z + sz * 0.5);
      if (Math.abs(cam.fov - fov) > 0.005) {
        cam.fov = fov;
        cam.updateProjectionMatrix();
      }
    },
  };
}
